import dayjs from 'dayjs'

import CloseOutlinedIcon from '@ant-design/icons/CloseOutlined'

import AppBar from '@mui/material/AppBar'
import Container from '@mui/material/Container'
import Dialog from '@mui/material/Dialog'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import { FeatureFlag, useFeatureFlag } from '@shared/providers/src/FeatureFlagsProvider'
import { Setting, useSetting } from '@shared/providers/src/SettingsProvider'
import { TreatmentType } from '@shared/utils/src/Constants'

import { useCBO } from '@pages/CBOs/CBO/CBO.hooks'
import { spacing } from '@components/Details/Details.utils'
import DocTitle from '@components/DocTitle'
import { SlideUp } from '@components/Transitions'

import { useCBORuleSets } from '../CBORuleSet.hooks'
import Contacts from './Contacts'
import Coverage from './Coverage'
import LHCDetails from './LHCDetails'
import Overview from './Overview'
import PatientCare from './PatientCare'
import PharmacyFinancials from './PharmacyFinancials'
import Program340BDetails from './Program340BDetails'
import Services from './Services'
import Team from './Team'

export default function CBORuleSetDetailsDialog({ id, slide = false, open, onClose }) {
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      slots={{ transition: slide ? SlideUp : undefined }}
      slotProps={{
        paper: {
          sx: { backgroundColor: 'background.default' },
        },
      }}
    >
      <CBODetails id={id} onClose={onClose} />
    </Dialog>
  )
}

function CBODetails({ id, onClose }) {
  const cashPayCBOs = useSetting(Setting.CashPayCBOs, JSON.parse)
  const cashPayEnabled = useFeatureFlag(FeatureFlag.CashPay)

  const { data: cbo, isPending: cboIsPending } = useCBO(id)
  const { data: ruleSets, isPending: ruleSetsArePending } = useCBORuleSets(id)

  const supportsCashPay = cashPayEnabled && cashPayCBOs.map((v) => v.toString()).includes(id)
  const providesHIV = cbo?.cbosProviderTypes?.some((type) => type.name === TreatmentType.HIV)

  return (
    <>
      <DocTitle title={cbo?.name || 'CBO Details'} />
      <AppBar>
        <Toolbar>
          <Typography variant="h4" sx={{ flex: 1 }}>
            {cbo?.name || <Skeleton width={200} />}
          </Typography>
          <Typography sx={{ mx: 2 }}>
            {ruleSets ? ` Last Updated: ${dayjs(ruleSets.updatedAt).format('LLL')}` : <Skeleton width={200} />}
          </Typography>
          <IconButton shape="rounded" color="inherit" onClick={onClose} data-testid="close-calendar">
            <CloseOutlinedIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: (theme) => `${theme.mixins.toolbar.height}px` }}>
        <Grid container spacing={spacing}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={spacing}>
              <Overview cbo={cbo} ruleSets={ruleSets} isLoading={cboIsPending} />
              <Team cbo={cbo} ruleSets={ruleSets} cboIsLoading={cboIsPending} ruleSetsAreLoading={ruleSetsArePending} />
              <Contacts data={ruleSets} isLoading={ruleSetsArePending} />
              <PharmacyFinancials cbo={cbo} ruleSets={ruleSets} cboIsLoading={cboIsPending} ruleSetsAreLoading={ruleSetsArePending} />
              <Coverage data={cbo} isLoading={cboIsPending} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={spacing}>
              <PatientCare
                supportsCashPay={supportsCashPay}
                ruleSets={ruleSets}
                ruleSetsAreLoading={ruleSetsArePending}
                cbo={cbo}
                cboIsLoading={cboIsPending}
              />
              <Services data={ruleSets} isLoading={ruleSetsArePending} />
              {providesHIV && <LHCDetails data={ruleSets} isLoading={ruleSetsArePending} />}
              <Program340BDetails data={ruleSets} isLoading={ruleSetsArePending} />
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </>
  )
}
